import { Routes } from 'Modulor/routes';
import { lazy, memo, Suspense, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getGBFeatures } from 'services/growthbook';
import { BONUS_EXP, BonusExpValue } from 'services/growthbook/constants';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { getBonusMinDepositAmount } from 'store/models';
import {
  bonusDataSelector,
  firstBonusSelector,
  isAllBonusesNotSelectedSelector,
  isAllowBonusSelectionSelector,
  setIsAllowBonusSelection,
  setSelectedBonusByExperiment,
} from 'store/slices/bonus';
import { currentDepositMethodSelector } from 'store/slices/deposit';
import { growthbookFeaturesSelector, isGrowthbookInitializedSelector } from 'store/slices/global';
import { submitRedirectBlankSelector } from 'store/slices/submitForm';
import { isMultiCurrencySelector, selectedCurrencySelector } from 'store/slices/wallet';
import { isPwContext } from 'utils/pwContext';

type BonusExpValueType = (typeof BonusExpValue)[keyof typeof BonusExpValue];

const isValidBonusExpValue = (value: unknown): value is BonusExpValueType =>
  (Object.values(BonusExpValue) as unknown[]).includes(value);

const getIsHiddenByExperiment = (isReady: boolean, expValue: string | undefined, allNotSelected: boolean): boolean => {
  if (!isReady) return false;
  if (!isValidBonusExpValue(expValue)) return true;
  if (expValue === BonusExpValue.HideBonus) return true;
  return expValue === BonusExpValue.SingleSelection && allNotSelected;
};

const BonusInfoLazy = lazy(() => import(/* webpackChunkName: "bonus-info" */ './BonusInfo'));

const BonusInfoSingleLazy = lazy(() => import(/* webpackChunkName: "bonus-info-single" */ './BonusInfoSingle'));

export const BONUS_DEFAULT_IMAGE_PATH = `${isPwContext() ? '/pw' : ''}/assets/default-bonus-icon.webp`;
export const BONUS_NEW_IMAGE_PATH = `${isPwContext() ? '/pw' : ''}/assets/bonus-icon-new.svg`;
/**
 * Additional logic in src/store/slices/bonus.ts
 * */

const gbFeatures = getGBFeatures();

const BonusInfoContainer = () => {
  const dispatch = useAppDispatch();
  const bonusData = useAppSelector(bonusDataSelector);
  const growthbookFeatures = useAppSelector(growthbookFeaturesSelector);
  const isGrowthbookInitialized = useAppSelector(isGrowthbookInitializedSelector);
  const isRedirectBlank = useAppSelector(submitRedirectBlankSelector);
  const isAllowBonusSelection = useAppSelector(isAllowBonusSelectionSelector);
  const firstBonus = useAppSelector(firstBonusSelector);
  const isAllBonusesNotSelected = useAppSelector(isAllBonusesNotSelectedSelector);
  const currentMethod = useAppSelector(currentDepositMethodSelector);
  const isMultiCurrency = useAppSelector(isMultiCurrencySelector);
  const currentCurrency = useAppSelector(selectedCurrencySelector);
  const location = useLocation();

  const isBonusExp = !!growthbookFeatures && growthbookFeatures.includes(BONUS_EXP);
  const gbExperimentResult = gbFeatures.growthBook.evalFeature(BONUS_EXP);
  const isGrowthbookUnavailable = isGrowthbookInitialized && gbExperimentResult.source === 'unknownFeature';
  const isExperimentReady = isGrowthbookInitialized && gbExperimentResult.source !== 'unknownFeature';
  const realPath = (location.state as { background?: { pathname: string } })?.background?.pathname || location.pathname;
  const isResultPage = [Routes.depositError, Routes.depositProcessing, Routes.depositSuccess].some((route) =>
    realPath.includes(route),
  );
  const { bonusInfo } = bonusData || {};
  const isDisplayFormPage = realPath.includes(Routes.depositDisplayForm);
  const isMultiCurrencyFiatDisplayForm = isMultiCurrency && currentCurrency?.isFiat && isDisplayFormPage;
  const isHiddenByExperiment = getIsHiddenByExperiment(
    isExperimentReady,
    gbExperimentResult?.value,
    isAllBonusesNotSelected,
  );
  const isVisible =
    bonusInfo &&
    !isResultPage &&
    !isRedirectBlank &&
    !isMultiCurrencyFiatDisplayForm &&
    (!isBonusExp || isExperimentReady || isGrowthbookUnavailable) &&
    !isHiddenByExperiment;

  useEffect(() => {
    if (!isBonusExp || !isExperimentReady) return;
    const expValue = gbExperimentResult?.value;
    switch (expValue) {
      case BonusExpValue.SingleSelection:
        dispatch(setIsAllowBonusSelection(false));
        break;
      case BonusExpValue.FirstBonusSelection:
        if (bonusData?.bonusesList?.length) {
          dispatch(setSelectedBonusByExperiment(bonusData.bonusesList[0]));
        }
        break;
      default:
        break;
    }
  }, [isBonusExp, isExperimentReady, gbExperimentResult?.value, bonusData?.bonusesList, dispatch]);

  if (!isVisible) return null;

  if (!isAllowBonusSelection) {
    const isMethodSpecificBonus = firstBonus?.payGroups && firstBonus.payGroups.length > 0;
    const shouldFilterByMethod = isMethodSpecificBonus && currentMethod;

    if (shouldFilterByMethod && !firstBonus?.payGroups?.includes(currentMethod.name)) return null;

    return (
      <Suspense fallback={null}>
        <BonusInfoSingleLazy
          bonusName={firstBonus.bonusName}
          bonusDescription={firstBonus.bonusDescription}
          infoLine={bonusInfo.info_line}
          bonusImageUrl={firstBonus.bonusImageUrl}
          minDepositAmount={getBonusMinDepositAmount(firstBonus)}
        />
      </Suspense>
    );
  }

  return (
    <Suspense fallback={null}>
      <BonusInfoLazy
        bonusName={bonusInfo.bonusName}
        bonusImageUrl={bonusInfo.bonusImageUrl}
        infoLine={bonusInfo.info_line}
        withoutBonus={bonusInfo.withoutBonus}
        minDepositAmount={getBonusMinDepositAmount(bonusInfo)}
        bonusCount={bonusData?.bonusesList?.length || 0}
      />
    </Suspense>
  );
};

export default memo(BonusInfoContainer);
